import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import DoughnutChart from './DoughnutChart';
import {
  computeHour,
  fmt,
  SPEECH_CATEGORY_NAME,
  MAX_ROWS_PER_HOUR,
} from './defaults';

// Responsive chart size. Kept as a hook so every ClockPanel instance
// adapts on its own (no props drilling). We fall back to a mid size
// during SSR.
function useResponsiveChartSize(desktop = 640) {
  const [w, setW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1200));
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setW(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  if (w < 480) return 260;
  if (w < 768) return 320;
  if (w < 1024) return 420;
  if (w < 1280) return 520;
  return desktop;
}

// Single 60-minute clock: sequence table (left) + big doughnut w/ clock
// markers (right, centered inside its half). readOnly=true switches
// input cells to plain text so PDF exports capture the values.
export default function ClockPanel({ title, rows, categories, speechTypes, onChange, readOnly = false, chartSize }) {
  const autoSize = useResponsiveChartSize();
  const effectiveSize = chartSize === 0 ? 0 : (chartSize || autoSize);
  const { total, remaining, over, segments } = computeHour(rows, categories, speechTypes);

  const setRow = (i, patch) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange(next);
  };
  const delRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const addRow = () => {
    if (rows.length >= MAX_ROWS_PER_HOUR) return;
    onChange([...rows, { cat: '', type: '' }]);
  };
  const clearRows = () => onChange([]);

  return (
    <div
      data-testid="clock-panel"
      className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 flex flex-col gap-3"
    >
      {title && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h4 className="text-sm font-bold text-[#1B2A4A] uppercase tracking-wide">{title}</h4>
          <div className="flex gap-2 text-xs">
            <span className="bg-slate-100 rounded px-2 py-1 text-slate-800">
              Σύνολο <b>{fmt(total)}</b>
            </span>
            <span className="bg-slate-100 rounded px-2 py-1 text-slate-800">
              Υπόλοιπο <b>{(remaining < 0 ? '-' : '') + fmt(Math.abs(remaining))}</b>
            </span>
            <span
              className={`rounded px-2 py-1 font-bold text-white ${over ? 'bg-red-700' : 'bg-emerald-600'}`}
            >
              {over ? 'OVER' : 'OK'}
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        {/* Sequence table (left, compact on desktop, full-width on mobile) */}
        <div className="flex-1 min-w-0 w-full lg:max-w-[48%]">
          <div className="border border-gray-200 rounded overflow-hidden">
            <table className="w-full text-[11px] border-collapse table-fixed">
              <colgroup>
                <col style={{ width: '28px' }} />
                <col />
                <col />
                <col style={{ width: '54px' }} />
                <col style={{ width: '54px' }} />
                <col style={{ width: '54px' }} />
                <col style={{ width: '28px' }} />
              </colgroup>
              <thead className="bg-[#1B2A4A] text-white">
                <tr>
                  <th className="px-1 py-1">#</th>
                  <th className="px-1 py-1 text-left">Κατηγορία</th>
                  <th className="px-1 py-1 text-left">Τύπος Ομιλίας</th>
                  <th className="px-1 py-1">Διάρκ.</th>
                  <th className="px-1 py-1">Από</th>
                  <th className="px-1 py-1">Έως</th>
                  <th className="px-1 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <ClockRow
                    key={i}
                    index={i}
                    row={r}
                    rows={rows}
                    categories={categories}
                    speechTypes={speechTypes}
                    onChange={(patch) => setRow(i, patch)}
                    onDelete={() => delRow(i)}
                    readOnly={readOnly}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {!readOnly && (
            <div className="flex gap-2 mt-2 flex-wrap">
              <button
                type="button"
                onClick={addRow}
                data-testid="clock-add-row-btn"
                disabled={rows.length >= MAX_ROWS_PER_HOUR}
                className="text-xs bg-[#1B2A4A] text-white px-2 py-1 rounded hover:opacity-90 disabled:opacity-40 flex items-center gap-1"
              >
                <Plus size={12} /> Σειρά
              </button>
              <button
                type="button"
                onClick={clearRows}
                data-testid="clock-clear-rows-btn"
                className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded hover:bg-slate-300"
              >
                Καθαρισμός
              </button>
              <span className="text-[10px] text-gray-500 self-center">
                {rows.length}/{MAX_ROWS_PER_HOUR} γραμμές
              </span>
            </div>
          )}
        </div>

        {/* Doughnut (right on desktop, centered inside remaining space).
            Passing chartSize=0 hides it entirely, useful for the PDF
            layout that renders the chart on its own at a much larger
            size. */}
        {effectiveSize > 0 && (
          <div className="flex-1 flex items-center justify-center w-full min-h-0">
            <DoughnutChart segments={segments} size={effectiveSize} showMarkers />
          </div>
        )}
      </div>
    </div>
  );
}

function ClockRow({ index, row, rows, categories, speechTypes, onChange, onDelete, readOnly }) {
  const prior = rows.slice(0, index);
  const priorAgg = computeHour(prior, categories, speechTypes);
  const from = priorAgg.total;
  const cat = categories.find((c) => c.name === row.cat);
  const isSpeech = row.cat === SPEECH_CATEGORY_NAME;
  let dur = 0;
  if (isSpeech && row.type) {
    const st = speechTypes.find((s) => s.name === row.type);
    if (st) dur = st.sec;
  } else if (cat) {
    dur = cat.sec;
  }
  const to = from + dur;

  // In read-only mode (PDF export), render plain spans so html2canvas
  // captures the selected values as visible text.
  return (
    <tr className={index % 2 ? 'bg-slate-50' : 'bg-white'}>
      <td className="px-1 py-0.5 text-center text-gray-500">{index + 1}</td>
      <td className="px-1 py-0.5">
        {readOnly ? (
          <span
            className="block text-[11px] text-slate-800 pl-1 whitespace-normal break-words leading-tight"
            style={cat ? { borderLeft: `4px solid ${cat.color}` } : {}}
          >
            {row.cat || '—'}
          </span>
        ) : (
          <select
            data-testid={`clock-row-${index}-cat`}
            value={row.cat}
            onChange={(e) => onChange({ cat: e.target.value })}
            className="w-full border border-gray-300 rounded px-1 py-0.5 text-[11px] text-slate-800 bg-white"
            style={cat ? { borderLeft: `4px solid ${cat.color}` } : {}}
          >
            <option value="">—</option>
            {categories.map((c) => (
              <option key={c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
        )}
      </td>
      <td className="px-1 py-0.5">
        {readOnly ? (
          <span className="block text-[11px] text-slate-800 pl-1 whitespace-normal break-words leading-tight">
            {isSpeech && row.type ? row.type : (isSpeech ? '—' : '')}
          </span>
        ) : isSpeech ? (
          <select
            data-testid={`clock-row-${index}-type`}
            value={row.type || ''}
            onChange={(e) => onChange({ type: e.target.value })}
            className="w-full border border-gray-300 rounded px-1 py-0.5 text-[11px] text-slate-800 bg-white"
          >
            <option value="">—</option>
            {speechTypes.map((s) => (
              <option key={s.name} value={s.name}>{s.name}</option>
            ))}
          </select>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-1 py-0.5 text-center font-mono text-slate-800">{dur ? fmt(dur) : ''}</td>
      <td className="px-1 py-0.5 text-center font-mono text-slate-800">{fmt(from)}</td>
      <td className="px-1 py-0.5 text-center font-mono text-slate-800">{fmt(to)}</td>
      <td className="px-1 py-0.5 text-center">
        {!readOnly && (
          <button
            type="button"
            onClick={onDelete}
            data-testid={`clock-row-${index}-del`}
            className="text-red-600 hover:text-red-800"
            title="Διαγραφή"
          >
            <Trash2 size={12} />
          </button>
        )}
      </td>
    </tr>
  );
}
